<template>
  <div class="panel-detalle">
    <!-- ── Cargando ── -->
    <div v-if="store.cargandoDetalle" class="estado-centro">
      <div class="spinner-border text-verde"></div>
      <p class="mt-3 text-cafe small">Cargando datos de la rutina...</p>
    </div>

    <!-- ── Detalle ── -->
    <template v-else-if="store.calendarioActivo">
      <!-- ═══ HEADER: un solo renglón ═══ -->
      <div class="detalle-header">
        <!-- Grupo izquierdo: identidad -->
        <div class="hd-left">
          <span :class="['badge-est', clsEst(store.calendarioActivo.estatus)]">
            <span
              class="dot-est"
              :style="{ background: colorEst(store.calendarioActivo.estatus) }"
            ></span>
            {{ labelEst(store.calendarioActivo.estatus) }}
          </span>
          <span
            v-if="store.calendarioActivo.rutina_tipo"
            :class="['badge-tipo', store.calendarioActivo.rutina_tipo?.toLowerCase()]"
          >
            {{ labelTipo(store.calendarioActivo.rutina_tipo) }}
          </span>
          <span v-if="estaAtrasado" class="badge-alerta">
            <i class="bi bi-exclamation-triangle-fill me-1"></i>Período vencido
          </span>
          <span class="hd-nombre">{{ store.calendarioActivo.maquina_nombre }}</span>
          <span class="hd-sep">·</span>
          <span class="hd-rutina">{{ store.calendarioActivo.rutina_nombre }}</span>
        </div>

        <!-- Grupo derecho: fechas + progreso + inicio -->
        <div class="hd-right">
          <span v-if="store.ejecucion?.fecha_inicio" class="hd-meta">
            <i class="bi bi-play-circle me-1"></i
            >{{ formatFechaHora(store.ejecucion.fecha_inicio) }}
          </span>
          <span class="hd-sep-v"></span>
          <span class="hd-meta">
            <i class="bi bi-calendar3 me-1 opacity-50"></i
            >{{ formatFecha(store.calendarioActivo.fecha_programada) }}
          </span>
          <span
            v-if="store.calendarioActivo.fecha_limite"
            :class="['hd-meta', { 'hd-meta-alerta': estaAtrasado }]"
          >
            <i class="bi bi-flag me-1 opacity-50"></i
            >{{ formatFecha(store.calendarioActivo.fecha_limite) }}
          </span>
          <span class="hd-sep-v"></span>
          <div v-if="store.ejecucion" class="hd-progreso">
            <div class="prog-bar">
              <div
                class="prog-fill"
                :style="{ width: store.progresoActividades.pct + '%' }"
              ></div>
            </div>
            <span class="prog-texto">
              {{ store.progresoActividades.completadas }}/{{
                store.progresoActividades.total
              }}
              ({{ store.progresoActividades.pct }}%)
            </span>
          </div>
        </div>
      </div>

      <!-- ═══ CUERPO EN DOS COLUMNAS ═══ -->
      <div class="detalle-cuerpo">
        <!-- COLUMNA IZQUIERDA: Actividades con scroll -->
        <div class="col-actividades">
          <div class="seccion-header">
            <span class="seccion-titulo">
              <i class="bi bi-list-check me-2"></i>Actividades
            </span>
            <span class="seccion-badge">
              {{ store.progresoActividades.completadas }}/{{
                store.progresoActividades.total
              }}
            </span>
          </div>

          <div v-if="store.ejecucion" class="actividades-scroll">
            <div
              v-for="act in actividadesOrdenadas"
              :key="act.id"
              :class="[
                'actividad-card',
                { completada: act.completado, reprogramada: act.reprogramada },
              ]"
            >
              <div class="act-fila-top">
                <div class="act-check-wrap">
                  <input
                    type="checkbox"
                    class="act-check"
                    :id="'act-' + act.id"
                    :checked="act.completado"
                    :disabled="act.completado || store.estaFinalizada || store.guardando"
                    @change="abrirObservacion(act)"
                  />
                </div>
                <label
                  :for="'act-' + act.id"
                  class="act-label"
                  :class="{ tachada: act.completado }"
                >
                  <span class="orden-dot">{{ act.orden }}</span>
                  {{ act.actividad_nombre }}
                </label>
                <div class="act-acciones">
                  <div
                    v-if="guardandoActividad === act.id"
                    class="spinner-border spinner-border-sm text-verde"
                  ></div>
                  <button
                    v-if="actDescripcion(act)"
                    class="btn-icon"
                    :title="actDescVisible[act.id] ? 'Ocultar' : 'Ver descripción'"
                    @click.stop="toggleDesc(act.id)"
                  >
                    <i
                      :class="
                        actDescVisible[act.id] ? 'bi bi-chevron-up' : 'bi bi-info-circle'
                      "
                    ></i>
                  </button>
                </div>
              </div>

              <div
                v-if="actDescripcion(act) && actDescVisible[act.id]"
                class="act-descripcion"
              >
                {{ actDescripcion(act) }}
              </div>
              <div v-if="act.observaciones" class="act-obs">
                <i class="bi bi-chat-left-text me-1"></i>{{ act.observaciones }}
              </div>
              <div v-if="act.reprogramada" class="act-reprog-info">
                <i class="bi bi-calendar2-check me-1"></i>
                Reprogramada → {{ formatFecha(act.nueva_fecha) }}
              </div>
            </div>
          </div>

          <!-- Panel observación inline -->
          <div v-if="actividadPendiente" class="obs-panel mt-2">
            <p class="obs-titulo">
              <i class="bi bi-pencil me-1"></i>
              Observaciones — <em>{{ actividadPendiente.actividad_nombre }}</em>
            </p>
            <textarea
              v-model="obsTexto"
              class="obs-textarea"
              rows="2"
              placeholder="Escribe observaciones opcionales..."
            ></textarea>
            <div class="obs-acciones">
              <button class="btn-cancelar" @click="actividadPendiente = null">
                Cancelar
              </button>
              <button
                class="btn-confirmar"
                @click="confirmarActividad"
                :disabled="store.guardando"
              >
                <i class="bi bi-check me-1"></i>Marcar completada
              </button>
            </div>
          </div>

          <!-- Panel reprogramar inline -->
          <div v-if="actividadReprogramando" class="reprog-panel mt-2">
            <p class="obs-titulo">
              <i class="bi bi-calendar2-plus me-1"></i>
              Reprogramar — <em>{{ actividadReprogramando.actividad_nombre }}</em>
            </p>
            <div class="reprog-info mb-2">
              <i class="bi bi-info-circle me-1"></i>
              Dentro del período actual (hasta
              {{ formatFecha(store.calendarioActivo?.fecha_limite) }}).
            </div>
            <div class="reprog-row">
              <input
                v-model="reprogFecha"
                type="date"
                class="reprog-input"
                :min="hoyStr"
                :max="store.calendarioActivo?.fecha_limite"
              />
              <input
                v-model="reprogMotivo"
                type="text"
                class="reprog-input"
                placeholder="Motivo (tiempo, refacción, etc.)"
              />
            </div>
            <div class="obs-acciones mt-2">
              <button class="btn-cancelar" @click="actividadReprogramando = null">
                Cancelar
              </button>
              <button
                class="btn-confirmar"
                @click="confirmarReprogramar"
                :disabled="!reprogFecha || !reprogMotivo || guardandoReprog"
              >
                <span
                  v-if="guardandoReprog"
                  class="spinner-border spinner-border-sm me-1"
                ></span>
                <i v-else class="bi bi-check me-1"></i>Guardar
              </button>
            </div>
          </div>
        </div>

        <!-- COLUMNA DERECHA: Refacciones + Evidencia con scroll propio -->
        <div class="col-lateral">
          <!-- Refacciones -->
          <div class="seccion-card">
            <div class="seccion-header">
              <span class="seccion-titulo"
                ><i class="bi bi-box2 me-2"></i>Refacciones</span
              >
            </div>
            <div v-if="refaccionesRutina.length" class="refacciones-scroll">
              <div v-for="r in refaccionesRutina" :key="r.refaccion_id" class="ref-row">
                <div class="ref-nombre">
                  <span class="ref-texto">{{ r.refaccion_nombre }}</span>
                  <span class="ref-est">est. {{ r.cantidad_rutina }}</span>
                </div>
                <div v-if="!store.estaFinalizada" class="ref-control">
                  <button
                    class="btn-ref"
                    @click="decrementarRefaccion(r)"
                    :disabled="r.cantidad_usada <= 0"
                  >
                    −
                  </button>
                  <span class="ref-valor">{{ r.cantidad_usada }}</span>
                  <button
                    class="btn-ref"
                    @click="incrementarRefaccion(r)"
                    :disabled="r.cantidad_usada >= r.cantidad_rutina"
                  >
                    +
                  </button>
                </div>
                <span v-else class="ref-final">× {{ r.cantidad_usada }}</span>
              </div>
            </div>
            <p v-else class="sin-datos">Sin refacciones definidas</p>
          </div>

          <!-- Evidencia -->
          <div class="seccion-card">
            <div class="seccion-header">
              <span class="seccion-titulo"
                ><i class="bi bi-images me-2"></i>Evidencia</span
              >
              <label v-if="!store.estaFinalizada" class="btn-foto">
                <i class="bi bi-camera me-1"></i>Agregar
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  class="d-none"
                  @change="subirImagenes"
                />
              </label>
            </div>
            <div v-if="store.ejecucion?.imagenes?.length" class="imagenes-grid">
              <div v-for="img in store.ejecucion.imagenes" :key="img.id" class="img-wrap">
                <img :src="img.imagen" class="img-thumb" @click="verImagen(img.imagen)" />
                <button
                  v-if="!store.estaFinalizada"
                  class="btn-remove-img"
                  @click.stop="eliminarImagen(img.id)"
                >
                  <i class="bi bi-x"></i>
                </button>
              </div>
            </div>
            <p v-else class="sin-datos">Sin imágenes registradas</p>
          </div>

          <!-- Notas cierre -->
          <div v-if="store.ejecucion?.notas" class="seccion-card">
            <div class="seccion-header">
              <span class="seccion-titulo"
                ><i class="bi bi-journal-text me-2"></i>Notas de cierre</span
              >
            </div>
            <p class="notas-texto">{{ store.ejecucion.notas }}</p>
          </div>
        </div>
      </div>

      <!-- ═══ FOOTER ═══ -->
      <div class="detalle-footer">
        <div v-if="store.estaFinalizada" class="completado-badge">
          <i class="bi bi-check-circle-fill me-2"></i>Ejecución finalizada
        </div>
        <button
          v-else
          class="btn-finalizar"
          @click="emit('abrirFinalizar')"
          :disabled="store.guardando"
        >
          <i class="bi bi-check-circle me-2"></i>Finalizar ejecución
        </button>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, reactive } from "vue";
import { useEjecucionStore } from "@/store/ejecucionStore";
import { reprogramarActividad } from "@/utils/api_backend";
import Swal from "sweetalert2";

const emit = defineEmits(["abrirFinalizar", "finalizado"]);
const store = useEjecucionStore();

const actividadPendiente = ref(null);
const obsTexto = ref("");
const guardandoActividad = ref(null);
const actDescVisible = reactive({});
const actividadReprogramando = ref(null);
const reprogFecha = ref("");
const reprogMotivo = ref("");
const guardandoReprog = ref(false);
const hoyStr = new Date().toISOString().split("T")[0];

// ── Computed ──────────────────────────────────────────────────
const actividadesOrdenadas = computed(() =>
  [...(store.ejecucion?.actividades || [])].sort((a, b) => a.orden - b.orden)
);
const estaAtrasado = computed(() => {
  const fl = store.calendarioActivo?.fecha_limite;
  if (!fl) return false;
  return new Date() > new Date(fl + "T23:59:59");
});
const refaccionesRutina = computed(() => {
  const rutinaRefs = store.calendarioActivo?.rutina_refacciones || [];
  const ejecucionRefs = store.ejecucion?.refacciones || [];
  return rutinaRefs.map((rr) => {
    const guardada = ejecucionRefs.find(
      (er) => er.refaccion_id === rr.refaccion || er.refaccion === rr.refaccion
    );
    return reactive({
      refaccion_id: rr.refaccion,
      refaccion_nombre: rr.refaccion_nombre,
      cantidad_rutina: rr.cantidad,
      cantidad_usada: guardada ? guardada.cantidad : 0,
    });
  });
});

// ── Helpers ───────────────────────────────────────────────────
const LABEL_ESTATUS = {
  PENDIENTE: "Pendiente",
  EN_PROCESO: "En proceso",
  COMPLETADO: "Completado",
  ATRASADO: "Atrasado",
};
const COLOR_ESTATUS = {
  PENDIENTE: "#d48804",
  EN_PROCESO: "#1565c0",
  COMPLETADO: "var(--d-green)",
  ATRASADO: "#cc3636",
};
const LABEL_TIPO = {
  CONTROL: "Control",
  ELECTROMECANICO: "Electromecánico",
  HIDRAULICO: "Hidráulico",
  MECANICO: "Mecánico",
  NEUMATICO: "Neumático",
};

const labelEst = (v) => LABEL_ESTATUS[v] || v || "—";
const colorEst = (v) => COLOR_ESTATUS[v] || "#888";
const clsEst = (v) => (v || "").toLowerCase().replace("_", "-");
const labelTipo = (v) => LABEL_TIPO[v] || v || "—";

function formatFecha(f) {
  if (!f) return "—";
  return new Date(f + "T12:00:00").toLocaleDateString("es-MX", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
function formatFechaHora(f) {
  if (!f) return "—";
  return new Date(f).toLocaleString("es-MX", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}
function actDescripcion(act) {
  return act.actividad_descripcion || "";
}
function toggleDesc(id) {
  actDescVisible[id] = !actDescVisible[id];
}

// ── Actividades ───────────────────────────────────────────────
function abrirObservacion(act) {
  actividadPendiente.value = act;
  obsTexto.value = "";
  actividadReprogramando.value = null;
}
async function confirmarActividad() {
  const act = actividadPendiente.value;
  if (!act) return;
  guardandoActividad.value = act.id;
  try {
    await store.completarActividad(act.id, obsTexto.value);
    actividadPendiente.value = null;
  } catch {
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "No se pudo completar la actividad.",
      confirmButtonColor: "var(--d-brown)",
    });
  } finally {
    guardandoActividad.value = null;
  }
}

// ── Reprogramar ───────────────────────────────────────────────
function abrirReprogramar(act) {
  actividadReprogramando.value = act;
  reprogFecha.value = "";
  reprogMotivo.value = "";
  actividadPendiente.value = null;
}
async function confirmarReprogramar() {
  if (!reprogFecha.value || !reprogMotivo.value) return;
  guardandoReprog.value = true;
  try {
    const ejecAct = actividadReprogramando.value;
    const calAct = store.calendarioActivo?.actividades?.find(
      (ca) => String(ca.rutina_actividad) === String(ejecAct.rutina_actividad)
    );
    if (!calAct) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se encontró la actividad en el calendario.",
        confirmButtonColor: "var(--d-brown)",
      });
      return;
    }
    await reprogramarActividad(
      store.calendarioActivo.id,
      calAct.id,
      reprogFecha.value,
      reprogMotivo.value
    );
    actividadReprogramando.value = null;
    Swal.fire({
      icon: "success",
      title: "Actividad reprogramada",
      text: `Nueva fecha: ${formatFecha(reprogFecha.value)}`,
      timer: 2000,
      showConfirmButton: false,
    });
  } catch (err) {
    const msg = err?.response?.data?.error || "No se pudo reprogramar la actividad.";
    Swal.fire({
      icon: "error",
      title: "Error",
      text: msg,
      confirmButtonColor: "var(--d-brown)",
    });
  } finally {
    guardandoReprog.value = false;
  }
}

// ── Refacciones ───────────────────────────────────────────────
function incrementarRefaccion(r) {
  if (r.cantidad_usada >= r.cantidad_rutina) return;
  r.cantidad_usada++;
  guardarRefaccion(r);
}
function decrementarRefaccion(r) {
  if (r.cantidad_usada <= 0) return;
  r.cantidad_usada--;
  guardarRefaccion(r);
}
async function guardarRefaccion(r) {
  try {
    await store.actualizarRefaccion(r.refaccion_id, r.cantidad_usada);
  } catch {
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "No se pudo actualizar la refacción.",
      confirmButtonColor: "var(--d-brown)",
    });
  }
}

// ── Imágenes ──────────────────────────────────────────────────
async function subirImagenes(e) {
  const files = Array.from(e.target.files);
  if (!files.length) return;
  try {
    for (const file of files) await store.agregarImagen(file);
  } catch {
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "No se pudieron subir las imágenes.",
      confirmButtonColor: "var(--d-brown)",
    });
  }
  e.target.value = "";
}
async function eliminarImagen(imgId) {
  const result = await Swal.fire({
    title: "¿Eliminar imagen?",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "var(--d-brown)",
    cancelButtonText: "Cancelar",
    confirmButtonText: "Eliminar",
  });
  if (!result.isConfirmed) return;
  try {
    await store.eliminarImagen(imgId);
  } catch {
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "No se pudo eliminar la imagen.",
      confirmButtonColor: "var(--d-brown)",
    });
  }
}
function verImagen(url) {
  window.open(url, "_blank");
}
</script>

<style scoped>
/* ── Root ── */
.panel-detalle {
  background: var(--d-cream);
  border-radius: 14px;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  gap: 0;
}

.estado-centro {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 1rem;
  color: var(--d-green);
}

.text-cafe {
  color: var(--d-brown);
}

.text-verde {
  color: var(--d-green);
}

/* ═══ HEADER — un solo renglón ═══ */
.detalle-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 0.6rem;
  border-bottom: 1px solid rgba(96, 108, 56, 0.15);
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
  /* en pantallas pequeñas cae en dos líneas */
}

/* Izquierda: badges + nombre */
.hd-left {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  flex: 1;
  min-width: 0;
}

.hd-nombre {
  font-size: 0.95rem;
  font-weight: 800;
  color: var(--d-brown);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.hd-sep {
  color: var(--d-brown);
  opacity: 0.3;
  font-weight: 400;
  flex-shrink: 0;
}

.hd-rutina {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--d-green);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Derecha: metadatos */
.hd-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  flex-wrap: wrap;
}

.hd-meta {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--d-brown);
  opacity: 0.65;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
}

.hd-meta-alerta {
  color: #842029;
  opacity: 1;
  font-weight: 700;
}

/* Separador vertical entre grupos */
.hd-sep-v {
  width: 1px;
  height: 14px;
  background: rgba(64, 34, 8, 0.15);
  flex-shrink: 0;
}

/* Barra de progreso inline */
.hd-progreso {
  display: flex;
  align-items: center;
  gap: 6px;
}

.prog-bar {
  width: 70px;
  height: 5px;
  background: rgba(96, 108, 56, 0.15);
  border-radius: 4px;
  overflow: hidden;
}

.prog-fill {
  height: 100%;
  background: var(--d-green);
  border-radius: 4px;
  transition: width 0.3s;
}

.prog-texto {
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
}

/* Badges */
.badge-est {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.7rem;
  font-weight: 800;
  padding: 2px 8px;
  border-radius: 20px;
  white-space: nowrap;
  flex-shrink: 0;
}

.dot-est {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.badge-est.pendiente {
  background: #fff3cd;
  color: #7a5a00;
}

.badge-est.en-proceso {
  background: #cfe2ff;
  color: #1565c0;
}

.badge-est.completado {
  background: #d1e7dd;
  color: #0f5132;
}

.badge-est.atrasado {
  background: #f8d7da;
  color: #842029;
}

.badge-tipo {
  display: inline-block;
  font-size: 0.68rem;
  font-weight: 800;
  padding: 2px 7px;
  border-radius: 10px;
  flex-shrink: 0;
}

.badge-tipo.electromecanico {
  background: #fff8e1;
  color: #f57f17;
}

.badge-tipo.hidraulico {
  background: #e3f2fd;
  color: #0277bd;
}

.badge-tipo.mecanico {
  background: #fce4ec;
  color: #880e4f;
}

.badge-tipo.neumatico {
  background: #f3e5f5;
  color: #6a1b9a;
}

.badge-tipo.control {
  background: #e8f4fd;
  color: #1565c0;
}

.badge-alerta {
  display: inline-flex;
  align-items: center;
  font-size: 0.68rem;
  font-weight: 800;
  padding: 2px 8px;
  border-radius: 10px;
  background: #f8d7da;
  color: #842029;
  flex-shrink: 0;
}

/* ═══ CUERPO ═══ */
.detalle-cuerpo {
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 1rem;
  flex: 1;
  min-height: 0;
  /* clave para que los hijos con scroll funcionen */
  align-items: start;
}

/* ── Secciones ── */
.seccion-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.seccion-titulo {
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--d-brown);
  opacity: 0.8;
  display: flex;
  align-items: center;
}

.seccion-badge {
  background: rgba(64, 34, 8, 0.1);
  color: var(--d-brown);
  font-size: 0.7rem;
  font-weight: 800;
  padding: 1px 8px;
  border-radius: 10px;
}

.seccion-card {
  background: white;
  border: 1px solid rgba(96, 108, 56, 0.15);
  border-radius: 12px;
  padding: 0.75rem 0.9rem;
  margin-bottom: 0.6rem;
}

.seccion-card:last-child {
  margin-bottom: 0;
}

/* ── Columna actividades ── */
.col-actividades {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* SCROLL de actividades — altura máxima fija */
.actividades-scroll {
  display: flex;
  flex-direction: column;
  gap: 5px;
  max-height: 340px;
  overflow-y: auto;
  padding-right: 4px;
  /* Scrollbar estilizada */
  scrollbar-width: thin;
  scrollbar-color: rgba(96, 108, 56, 0.3) transparent;
}

.actividades-scroll::-webkit-scrollbar {
  width: 4px;
}

.actividades-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.actividades-scroll::-webkit-scrollbar-thumb {
  background: rgba(96, 108, 56, 0.3);
  border-radius: 4px;
}

.actividad-card {
  background: white;
  border: 1px solid rgba(96, 108, 56, 0.15);
  border-radius: 10px;
  padding: 8px 10px;
  flex-shrink: 0;
}

.actividad-card.completada {
  background: rgba(96, 108, 56, 0.04);
  border-color: rgba(96, 108, 56, 0.2);
}

.actividad-card.reprogramada {
  border-left: 3px solid #1565c0;
}

.act-fila-top {
  display: flex;
  align-items: center;
  gap: 8px;
}

.act-check-wrap {
  padding-top: 1px;
  flex-shrink: 0;
}

.act-check {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: var(--d-green);
}

.act-label {
  flex: 1;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--d-brown);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  line-height: 1.3;
}

.act-label.tachada {
  text-decoration: line-through;
  opacity: 0.45;
}

.orden-dot {
  background: var(--d-green);
  color: var(--d-cream);
  font-size: 0.6rem;
  font-weight: 800;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.act-acciones {
  display: flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
}

.btn-icon {
  background: none;
  border: none;
  color: var(--d-green);
  font-size: 0.78rem;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;
  opacity: 0.55;
}

.btn-icon:hover {
  background: rgba(96, 108, 56, 0.12);
  opacity: 1;
}

.btn-icon.btn-reprog {
  color: #1565c0;
}

.btn-icon.btn-reprog:hover {
  background: rgba(21, 101, 192, 0.1);
}

.act-descripcion {
  font-size: 0.77rem;
  color: #5a5a5a;
  line-height: 1.4;
  background: rgba(96, 108, 56, 0.05);
  border-left: 2px solid var(--d-green);
  border-radius: 0 4px 4px 0;
  padding: 3px 8px;
  margin-top: 5px;
}

.act-obs {
  font-size: 0.73rem;
  color: var(--d-green);
  font-style: italic;
  margin-top: 3px;
}

.act-reprog-info {
  display: inline-flex;
  align-items: center;
  background: #e3f2fd;
  color: #1565c0;
  font-size: 0.71rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  margin-top: 3px;
}

/* Paneles inline */
.obs-panel,
.reprog-panel {
  background: rgba(96, 108, 56, 0.06);
  border: 1px solid rgba(96, 108, 56, 0.2);
  border-radius: 10px;
  padding: 0.75rem 0.9rem;
}

.reprog-panel {
  background: rgba(21, 101, 192, 0.04);
  border-color: rgba(21, 101, 192, 0.2);
}

.obs-titulo {
  font-size: 0.81rem;
  font-weight: 700;
  color: var(--d-brown);
  margin-bottom: 0.4rem;
}

.obs-textarea {
  width: 100%;
  border: 1px solid rgba(96, 108, 56, 0.35);
  border-radius: 8px;
  padding: 5px 10px;
  font-size: 0.82rem;
  color: var(--d-brown);
  resize: none;
  outline: none;
  background: white;
  margin-bottom: 0.4rem;
}

.obs-textarea:focus {
  border-color: var(--d-green);
}

.reprog-info {
  font-size: 0.77rem;
  color: #1565c0;
  background: rgba(21, 101, 192, 0.06);
  border-radius: 6px;
  padding: 3px 10px;
}

.reprog-row {
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 8px;
}

.reprog-input {
  border: 1px solid rgba(96, 108, 56, 0.35);
  border-radius: 8px;
  padding: 5px 10px;
  font-size: 0.82rem;
  color: var(--d-brown);
  outline: none;
  background: white;
  width: 100%;
}

.reprog-input:focus {
  border-color: var(--d-green);
}

.obs-acciones {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.btn-cancelar {
  background: none;
  border: 1px solid rgba(96, 108, 56, 0.35);
  color: var(--d-green);
  border-radius: 7px;
  padding: 4px 12px;
  font-size: 0.81rem;
  font-weight: 600;
  cursor: pointer;
}

.btn-confirmar {
  background: var(--d-green);
  color: var(--d-cream);
  border: none;
  border-radius: 7px;
  padding: 4px 14px;
  font-size: 0.81rem;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  transition: background 0.15s;
}

.btn-confirmar:hover:not(:disabled) {
  background: var(--d-green-dark);
}

.btn-confirmar:disabled {
  opacity: 0.5;
  cursor: default;
}

/* ── Columna lateral ── */
.col-lateral {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.sin-datos {
  font-size: 0.79rem;
  color: var(--d-brown);
  opacity: 0.4;
  text-align: center;
  padding: 0.4rem;
}

/* SCROLL de refacciones — altura máxima fija */
.refacciones-scroll {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 180px;
  overflow-y: auto;
  padding-right: 2px;
  scrollbar-width: thin;
  scrollbar-color: rgba(96, 108, 56, 0.3) transparent;
}

.refacciones-scroll::-webkit-scrollbar {
  width: 4px;
}

.refacciones-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.refacciones-scroll::-webkit-scrollbar-thumb {
  background: rgba(96, 108, 56, 0.3);
  border-radius: 4px;
}

.ref-row {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(96, 108, 56, 0.04);
  border-radius: 8px;
  padding: 6px 10px;
  border: 1px solid rgba(96, 108, 56, 0.1);
  flex-shrink: 0;
}

.ref-nombre {
  flex: 1;
  min-width: 0;
}

.ref-texto {
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--d-brown);
  display: block;
}

.ref-est {
  font-size: 0.69rem;
  color: var(--d-green);
  opacity: 0.6;
}

.ref-control {
  display: flex;
  align-items: center;
  gap: 3px;
  background: rgba(96, 108, 56, 0.1);
  border-radius: 6px;
  padding: 1px 3px;
}

.btn-ref {
  background: none;
  border: none;
  color: var(--d-green);
  font-size: 1rem;
  font-weight: 800;
  line-height: 1;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}

.btn-ref:hover:not(:disabled) {
  background: rgba(96, 108, 56, 0.2);
}

.btn-ref:disabled {
  opacity: 0.3;
  cursor: default;
}

.ref-valor {
  font-size: 0.84rem;
  font-weight: 800;
  color: var(--d-brown);
  min-width: 20px;
  text-align: center;
}

.ref-final {
  font-size: 0.81rem;
  font-weight: 800;
  color: var(--d-green);
}

/* Imágenes */
.btn-foto {
  background: none;
  border: 1px solid var(--d-green);
  color: var(--d-green);
  border-radius: 6px;
  font-size: 0.71rem;
  font-weight: 700;
  padding: 2px 8px;
  cursor: pointer;
  transition: all 0.15s;
  display: inline-flex;
  align-items: center;
}

.btn-foto:hover {
  background: var(--d-green);
  color: var(--d-cream);
}

.imagenes-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.img-wrap {
  position: relative;
  display: inline-block;
}

.img-thumb {
  width: 58px;
  height: 58px;
  object-fit: cover;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid rgba(96, 108, 56, 0.2);
  transition: transform 0.15s;
  display: block;
}

.img-thumb:hover {
  transform: scale(1.08);
}

.btn-remove-img {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 17px;
  height: 17px;
  background: #842029;
  color: white;
  border: none;
  border-radius: 50%;
  font-size: 0.62rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  opacity: 0;
  transition: opacity 0.15s;
}

.img-wrap:hover .btn-remove-img {
  opacity: 1;
}

.notas-texto {
  font-size: 0.82rem;
  color: var(--d-brown);
  line-height: 1.5;
  margin: 0;
}

/* ═══ FOOTER ═══ */
.detalle-footer {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(96, 108, 56, 0.15);
  display: flex;
  justify-content: flex-end;
}

.btn-finalizar {
  background: var(--d-brown);
  color: var(--d-cream);
  border: none;
  font-weight: 700;
  border-radius: 8px;
  padding: 0.5rem 2rem;
  font-size: 0.88rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  transition: background 0.2s;
}

.btn-finalizar:hover:not(:disabled) {
  background: var(--d-green-dark);
}

.btn-finalizar:disabled {
  opacity: 0.5;
  cursor: default;
}

.completado-badge {
  background: #d1e7dd;
  color: #0f5132;
  border-radius: 8px;
  padding: 0.45rem 1.5rem;
  font-size: 0.86rem;
  font-weight: 700;
}

/* ── Responsive ── */
@media (max-width: 900px) {
  .detalle-cuerpo {
    grid-template-columns: 1fr;
  }

  .col-lateral {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.6rem;
  }

  .col-lateral > * {
    flex: 1;
    min-width: 220px;
  }

  .detalle-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .hd-right {
    flex-wrap: wrap;
  }

  .reprog-row {
    grid-template-columns: 1fr;
  }
}
</style>
